import React, { useState, useEffect } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { TextField } from '@mui/material';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const RoleForm = ({ isOpen, onClose, onSubmit, editData = null }) => {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const isEditMode = !!editData;

  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Initialize form data when editData changes
  useEffect(() => {
    if (editData) {
      setFormData({
        name: editData.name || '',
        description: editData.description || ''
      });
    } else {
      setFormData({
        name: '',
        description: ''
      });
    }
    setError('');
  }, [editData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setShowValidation(true);
    
    // Validation
    const requiredFields = ['name', 'description'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setShowFormLoading(true);
    
    try {
      console.log(`${isEditMode ? 'Editing' : 'Creating'} role with data:`, {
        name: formData.name,
        description: formData.description,
        isEditMode
      });

      // Call modules API and store response
      const modulesResponse = await dataAPI.getModules({ showLoading: false });
      console.log('Modules API response:', modulesResponse);
      
      // Store the modules response in a variable
      const modulesData = modulesResponse.data || [];
      
             // Navigate to RolePermission page
       navigate('/manage-employee/role-permission', { 
         state: { 
           modulesData, 
           roleName: formData.name,
           roleDescription: formData.description,
           roleType: 'company'
         } 
       });
      
      setIsSubmitting(false);
      setShowFormLoading(false);
      
    } catch (error) {
      console.error(`${isEditMode ? 'Edit' : 'Create'} error:`, error);
      setError(error.message || `Failed to ${isEditMode ? 'edit' : 'create'} role. Please try again.`);
    } finally {
      setIsSubmitting(false);
      setShowFormLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: ''
    });
    setError('');
    setShowFormLoading(false);
    setShowValidation(false);
    onClose();
  };

  // Check if all required fields are filled
  const requiredFields = ['name', 'description'];
  const isFormComplete = requiredFields.every(field => formData[field]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000,
          backdropFilter: 'blur(2px)'
        }}
      />
      
      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        width: '420px',
        height: 'calc(100vh - 40px)',
        backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
        zIndex: 1001,
        overflowY: 'auto',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.3s ease-in-out',
        borderRadius: '12px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 20px 8px 20px',
          borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: mode === 'dark' ? '#FFFFFF' : '#222222',
            margin: 0,
            fontFamily: 'Urbanist, Arial, sans-serif'
          }}>
            {isEditMode ? 'Edit Role' : 'Create Role'}
          </h2>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              color: mode === 'dark' ? '#FFFFFF' : '#666666',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CloseIcon style={{ fontSize: '20px' }} />
          </button>
        </div>

        {/* Form Loading Overlay */}
        {showFormLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1003,
            backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px'
          }}>
            {/* Fill Animation Loading Indicator */}
            <div className="fill-animation-container">
              {/* Background container */}
              <div className="fill-animation-background" />
              
              {/* Progress fill animation */}
              <div className="fill-animation-progress" />
              
              {/* AppIconBlue with pulse animation */}
              <img 
                src={AppIconBlue} 
                alt="Loading" 
                className={`fill-animation-icon pulse-animation-icon`}
              />
            </div>
          </div>
        )}

        {/* Form Content */}
        <form onSubmit={handleSubmit} style={{ padding: '16px 20px' }}>
          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#ffebee',
              border: '1px solid #f44336',
              borderRadius: '6px',
              color: '#d32f2f',
              fontSize: '14px',
              fontFamily: 'Urbanist, Arial, sans-serif'
            }}>
              {error}
            </div>
          )}

          {/* Role Name */}
          <div style={{ marginBottom: '16px' }}>
            <TextField
              size="small"
              required
              label="Role Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              disabled={isSubmitting}
              error={showValidation && !formData.name.trim()}
              helperText={showValidation && !formData.name.trim() ? 'Role name is required' : ''}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
              }}
            />
          </div>

          {/* Role Description */}
          <div style={{ marginBottom: '20px' }}>
            <TextField
              size="small"
              required
              label="Role Description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={isSubmitting}
              error={showValidation && !formData.description.trim()}
              helperText={showValidation && !formData.description.trim() ? 'Role description is required' : ''}
              multiline
              rows={4}
              style={{
                width: '100%',
                backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                color: mode === 'dark' ? '#FFFFFF' : '#222222'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: mode === 'dark' ? '#444' : '#ddd',
                  },
                  '&:hover fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#26C3C0',
                    boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: mode === 'dark' ? '#999' : '#666',
                  fontSize: '14px',
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  fontSize: '12px',
                },
              }}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || !isFormComplete}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: isFormComplete && !isSubmitting ? '#26C3C0' : (mode === 'dark' ? '#444' : '#666'),
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: isFormComplete && !isSubmitting ? 'pointer' : 'not-allowed',
              fontFamily: 'Urbanist, Arial, sans-serif',
              transition: 'background-color 0.2s ease',
              boxShadow: isFormComplete && !isSubmitting ? '0 2px 8px rgba(38, 195, 192, 0.3)' : 'none',
              opacity: isSubmitting ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (isFormComplete && !isSubmitting) {
                e.target.style.backgroundColor = '#22B0AD';
              } else {
                e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#777';
              }
            }}
            onMouseLeave={(e) => {
              if (isFormComplete && !isSubmitting) {
                e.target.style.backgroundColor = '#26C3C0';
              } else {
                e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#666';
              }
            }}
          >
            {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Role' : 'Create Role')}
          </button>
        </form>
      </div>
    </>
  );
};

export default RoleForm;